import React, { useState, useRef, useEffect, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useAuth } from '../auth/AuthContext';
import {
  HomeIcon,
  DocumentTextIcon,
  CogIcon,
  AdjustmentsHorizontalIcon,
  ScissorsIcon,
  BeakerIcon,
  Bars3Icon,
  XMarkIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  UserIcon,
  ArrowRightEndOnRectangleIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  DocumentMagnifyingGlassIcon,
  PuzzlePieceIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassCircleIcon,
  FolderIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

// Grouped navigation structure
const navigationGroups = [
  { name: 'Dashboard', href: '/', icon: HomeIcon, permission: null },
  {
    name: 'Classify', icon: DocumentTextIcon, permission: 'classify_documents',
    children: [
      { name: 'Classify', href: '/classify', icon: DocumentTextIcon },
      { name: 'Review', href: '/review', icon: MagnifyingGlassIcon },
      { name: 'Mosaic', href: '/mosaic', icon: PuzzlePieceIcon },
    ]
  },
  { name: 'Sanitize', href: '/tearline', icon: ScissorsIcon, permission: 'tearline_sanitize' },
  {
    name: 'FOIA', icon: EyeSlashIcon, permission: 'foia_process',
    children: [
      { name: 'Process', href: '/foia', icon: EyeSlashIcon },
      { name: 'Discovery', href: '/foia-discovery', icon: DocumentMagnifyingGlassIcon },
      { name: 'Policy', href: '/foia-policy', icon: ShieldCheckIcon },
    ]
  },
  {
    name: 'Intel', icon: CogIcon,
    children: [
      { name: 'SCG Manager', href: '/scg', icon: CogIcon, permission: 'manage_scg' },
      { name: 'Search', href: '/search', icon: MagnifyingGlassCircleIcon, permission: 'classify_documents' },
      { name: 'Documents', href: '/documents', icon: FolderIcon, permission: 'classify_documents' },
      { name: 'Ask SCG', href: '/ask-scg', icon: ChatBubbleLeftRightIcon, permission: 'classify_documents' },
      { name: 'Declass Review', href: '/declass-review', icon: ClockIcon, permission: 'classify_documents' },
      { name: 'Provenance', href: '/provenance', icon: ClipboardDocumentListIcon, permission: 'classify_documents' },
    ]
  },
  {
    name: 'Admin', icon: AdjustmentsHorizontalIcon,
    children: [
      { name: 'Testing', href: '/testing', icon: BeakerIcon, permission: 'classify_documents' },
      { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, permission: 'classify_documents' },
      { name: 'History', href: '/history', icon: ClockIcon },
      { name: 'Feedback', href: '/feedback', icon: ChatBubbleLeftRightIcon, permission: 'manage_configs' },
      { name: 'Audit', href: '/audit', icon: ClipboardDocumentListIcon, permission: 'manage_users' },
      { name: 'Settings', href: '/settings', icon: AdjustmentsHorizontalIcon, permission: 'manage_configs' },
      { name: 'Users', href: '/users', icon: UserIcon, permission: 'manage_users' },
    ]
  },
];

export default function Navbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, hasPermission, logout } = useAuth();
  const userMenuRef = useRef(null);

  // Filter children based on permissions
  const getVisibleChildren = (children) => {
    if (!children) return [];
    return children.filter(child => !child.permission || hasPermission(child.permission));
  };

  // Filter groups: show if direct permission passes, or if any child is visible
  const visibleGroups = navigationGroups.filter(group => {
    if (group.children) {
      return getVisibleChildren(group.children).length > 0;
    }
    return !group.permission || hasPermission(group.permission);
  });

  // Check if any child route of a group is active
  const isGroupActive = (group) => {
    if (group.href) {
      return group.href === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(group.href);
    }
    if (group.children) {
      return group.children.some(child =>
        location.pathname === child.href ||
        (child.href !== '/' && location.pathname.startsWith(child.href))
      );
    }
    return false;
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="vault-nav">
      <div className="max-w-[1600px] mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Left - Logo */}
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-8 h-8 rounded flex items-center justify-center bg-[var(--vault-elevated)] border border-[var(--vault-border)] group-hover:border-[var(--spotlight-gold)] transition-colors">
                <img
                  src="/declass-logo.png"
                  alt="DECLASS"
                  className="h-5 w-5"
                />
              </div>
              <span className="text-sm font-bold tracking-wider text-[var(--text-primary)]">DECLASS</span>
            </Link>
          </div>

          {/* Center - Grouped Navigation Tabs */}
          <div className="hidden lg:flex items-center">
            <div className="flex items-end gap-0.5">
              {visibleGroups.map((group) => {
                const active = isGroupActive(group);

                // Direct link (no children)
                if (!group.children) {
                  return (
                    <Link
                      key={group.name}
                      to={group.href}
                      className={`folder-tab ${active ? 'active' : ''}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <group.icon className="w-3.5 h-3.5" />
                        <span>{group.name}</span>
                      </div>
                    </Link>
                  );
                }

                // Dropdown group
                const visibleChildren = getVisibleChildren(group.children);
                return (
                  <Menu as="div" key={group.name} className="relative">
                    <Menu.Button
                      className={`folder-tab ${active ? 'active' : ''}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <group.icon className="w-3.5 h-3.5" />
                        <span>{group.name}</span>
                        <ChevronDownIcon className="w-3 h-3 opacity-50" />
                      </div>
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute left-0 mt-0 vault-nav-dropdown">
                        {visibleChildren.map((child) => {
                          const childActive = location.pathname === child.href;
                          return (
                            <Menu.Item key={child.name}>
                              {({ active: hovered }) => (
                                <Link
                                  to={child.href}
                                  className={`vault-nav-dropdown-item ${childActive ? 'active' : ''} ${hovered && !childActive ? 'bg-[var(--vault-surface)]' : ''}`}
                                >
                                  <child.icon className="w-4 h-4" />
                                  <span>{child.name}</span>
                                </Link>
                              )}
                            </Menu.Item>
                          );
                        })}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                );
              })}
            </div>
          </div>

          {/* Right - User Menu */}
          <div className="flex items-center gap-4">
            {/* User dropdown - Desktop */}
            <div className="hidden md:block relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded bg-[var(--vault-elevated)] border border-[var(--vault-border)] hover:border-[var(--spotlight-gold)] transition-colors"
              >
                <div className="w-6 h-6 rounded bg-[var(--spotlight-glow)] border border-[var(--spotlight-gold)] flex items-center justify-center">
                  <span className="text-xs font-bold text-[var(--spotlight-gold)]">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-sm font-medium text-[var(--text-primary)]">{user?.username}</span>
                <ChevronDownIcon className={`w-4 h-4 text-[var(--text-muted)] transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* User dropdown menu */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 vault-card-elevated z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-[var(--vault-border)]">
                    <div className="text-sm font-medium text-[var(--text-primary)]">{user?.full_name}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">{user?.email}</div>
                    <div className="mt-2">
                      <span className="stamp stamp-unclassified text-[9px] py-0.5 px-2">
                        {user?.role?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 text-sm text-[var(--text-secondary)] hover:bg-[var(--vault-bg)] hover:text-[var(--error)] transition-colors flex items-center gap-2"
                  >
                    <ArrowRightEndOnRectangleIcon className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden vault-btn-ghost p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="h-5 w-5" />
              ) : (
                <Bars3Icon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu - grouped */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[var(--vault-border)]" id="mobile-menu">
            <div className="py-4 space-y-1">
              {/* User info - Mobile */}
              <div className="px-4 py-3 mb-4 vault-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-[var(--spotlight-glow)] border border-[var(--spotlight-gold)] flex items-center justify-center">
                    <span className="text-lg font-bold text-[var(--spotlight-gold)]">
                      {user?.username?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[var(--text-primary)]">{user?.full_name}</div>
                    <div className="text-xs text-[var(--text-muted)]">{user?.email}</div>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="mt-4 w-full vault-btn vault-btn-secondary text-[var(--error)] border-[var(--error)] hover:bg-[rgba(160,64,64,0.1)]"
                >
                  <ArrowRightEndOnRectangleIcon className="w-4 h-4" />
                  Sign Out
                </button>
              </div>

              {/* Grouped navigation items */}
              {visibleGroups.map((group) => {
                // Direct link (no children)
                if (!group.children) {
                  const isActive = group.href === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(group.href);
                  return (
                    <Link
                      key={group.name}
                      to={group.href}
                      className={`flex items-center gap-3 px-4 py-3 border-l-2 transition-colors ${
                        isActive
                          ? 'border-[var(--spotlight-gold)] bg-[var(--spotlight-glow)] text-[var(--spotlight-gold)]'
                          : 'border-transparent text-[var(--text-secondary)] hover:bg-[var(--vault-elevated)] hover:text-[var(--text-primary)]'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <group.icon className="w-5 h-5" />
                      <span className="text-sm font-medium uppercase tracking-wide">{group.name}</span>
                    </Link>
                  );
                }

                // Group with children
                const visibleChildren = getVisibleChildren(group.children);
                const groupActive = isGroupActive(group);
                return (
                  <div key={group.name}>
                    <div className={`flex items-center gap-3 px-4 py-2 mt-3 text-xs font-semibold uppercase tracking-widest ${
                      groupActive ? 'text-[var(--spotlight-gold)]' : 'text-[var(--text-muted)]'
                    }`}>
                      <group.icon className="w-4 h-4" />
                      <span>{group.name}</span>
                    </div>
                    {visibleChildren.map((child) => {
                      const childActive = location.pathname === child.href;
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`flex items-center gap-3 px-4 pl-11 py-2.5 border-l-2 transition-colors ${
                            childActive
                              ? 'border-[var(--spotlight-gold)] bg-[var(--spotlight-glow)] text-[var(--spotlight-gold)]'
                              : 'border-transparent text-[var(--text-secondary)] hover:bg-[var(--vault-elevated)] hover:text-[var(--text-primary)]'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <child.icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{child.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
